/** Formateadores y etiquetas de presentación de Cargadores (US-2.2, es-CO). */
import type {
  ConnectorStandard,
  OcppVersion,
  PowerType,
  StationOperationReason,
  StationStatus,
  StationSyncStatus,
} from '@goelectric/shared-types';

/** Estado del registro del cargador. */
export const STATION_STATUS_LABELS: Record<StationStatus, string> = {
  ACTIVE: 'Activo',
  INACTIVE: 'Dado de baja',
};

export const STATION_SYNC_STATUS_LABELS: Record<StationSyncStatus, string> = {
  PENDIENTE: 'Pendiente',
  SYNCED: 'Sincronizado',
  ERROR: 'Error de sincronización',
};

/** Motivo de `No disponible` (se muestra junto al badge de operación). */
export const OPERATION_REASON_LABELS: Record<StationOperationReason, string> = {
  FAULT: 'Falla del cargador',
  OUT_OF_SERVICE: 'Fuera de servicio',
  RESERVED: 'Reservado',
  PREPARING: 'Vehículo conectado, esperando inicio',
  NO_STATUS: 'Sin reporte de estado',
};

/** Estándar del conector físico. */
export const CONNECTOR_STANDARD_LABELS: Record<ConnectorStandard, string> = {
  TIPO_2: 'Tipo 2',
  CCS2: 'CCS2',
};

/** Versión de protocolo OCPP (el sufijo que el operador reconoce). */
export const OCPP_VERSION_LABELS: Record<OcppVersion, string> = {
  'ocpp1.6': '1.6J',
  'ocpp2.0.1': '2.0.1',
};

/** Tipo de potencia del conector (AC/DC). */
export const POWER_TYPE_LABELS: Record<PowerType, string> = {
  AC_1_PHASE: 'AC monofásica',
  AC_3_PHASE: 'AC trifásica',
  DC: 'DC',
};

/** Opciones del selector de estándar (Tipo 2 / CCS2). Compartidas por alta y modal. */
export const CONNECTOR_STANDARD_OPTIONS = (['TIPO_2', 'CCS2'] as const).map((value) => ({
  value,
  label: CONNECTOR_STANDARD_LABELS[value],
}));

/** Opciones del selector de tipo de potencia. Compartidas por alta y modal. */
export const POWER_TYPE_OPTIONS = (['AC_1_PHASE', 'AC_3_PHASE', 'DC'] as const).map((value) => ({
  value,
  label: POWER_TYPE_LABELS[value],
}));

/** Fecha/hora corta local (es-CO) para el detalle. Espejo del formateador de Ubicaciones. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Identidad visible del cargador: **`Ubicación · ChargePoint ID`** (ej. `Centro Comercial
 * Santafé · CP-001`). NO existe campo `name`: el equipo se nombra por dónde está y qué
 * ChargePoint ID tiene. Si falta el nombre de la ubicación, cae al ID solo.
 */
export function stationDisplayName(station: {
  id: string;
  location?: { name?: string } | null;
}): string {
  const locationName = station.location?.name?.trim();
  return locationName ? `${locationName} · ${station.id}` : station.id;
}
